import { z } from "zod";
import { accountStateSchema, platformInvoiceSchema } from "./billing.js";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/** Super Admin (docs/specs/super-admin.md, SA-14 … SA-19): platform admins, tenants, support, flags, announcements. */
const reason = z.string().trim().min(1).max(500);
const tenantStatus = z.enum(["trial", "active", "restricted", "suspended", "closed"]);
const platform = { platformAdmin: true };
const tag = { tag: "platform-admin" };
// Sign-in -------------------------------------------------------------------------------------------------------
export const platformSignInRequestSchema = z.object({
    identifier: z.string().trim().min(1).max(254),
    password: z.string().min(1).max(1024),
    /**
     * The authenticator (or a recovery) code: needed when the admin has two-step sign-in on. Two-step sign-in is
     * optional unless the Super Admin settings require it (owner decision 2026-11-02, ADR-0027).
     */
    code: z.union([z.literal(""), z.string().trim().min(6).max(20)]).optional(),
});
export const platformAdminSchema = z.object({
    userId: z.uuid(),
    name: z.string(),
    role: z.enum(["owner", "operator"]),
});
export const platformSessionSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: z.iso.datetime(),
    admin: platformAdminSchema,
});
export const platformSignIn = defineEndpoint({
    ...tag,
    name: "platformSignIn",
    method: "POST",
    path: "/v1/platform/auth/sign-in",
    access: { public: true },
    summary: "Platform admin sign-in: password, and the TOTP code when the admin has two-step sign-in on",
    body: platformSignInRequestSchema,
    status: 200,
    response: platformSessionSchema,
    errors: ["INVALID_CREDENTIALS", "INVALID_OTP", "RATE_LIMITED"],
});
export const platformRefresh = defineEndpoint({
    ...tag,
    name: "platformRefresh",
    method: "POST",
    path: "/v1/platform/auth/refresh",
    access: { public: true },
    summary: "New platform token from the refresh cookie (rotates it; the session ends 12 h after sign-in)",
    body: z.object({}).strict(),
    status: 200,
    response: platformSessionSchema,
    errors: ["INVALID_REFRESH_TOKEN"],
});
export const platformSignOut = defineEndpoint({
    ...tag,
    name: "platformSignOut",
    method: "POST",
    path: "/v1/platform/auth/sign-out",
    access: { public: true },
    summary: "End the platform session of the refresh cookie",
    body: z.object({}).strict(),
    status: 204,
});
export const getPlatformMe = defineEndpoint({
    ...tag,
    name: "getPlatformMe",
    method: "GET",
    path: "/v1/platform/me",
    access: platform,
    summary: "The signed-in platform admin",
    status: 200,
    response: platformAdminSchema,
});
// Tenants -------------------------------------------------------------------------------------------------------
export const platformTenantSchema = z.object({
    id: z.uuid(),
    slug: z.string(),
    name: z.string(),
    status: tenantStatus,
    planKey: z.string(),
    /** The subscription's price per month (yearly ÷ 12); 0 without a paid subscription. */
    mrrPaisa: paisa(),
    createdAt: z.iso.datetime(),
    trialEndsAt: z.iso.datetime().nullable(),
    lastActivityAt: z.iso.datetime().nullable(),
});
export const platformTenantListSchema = z.object({
    data: z.array(platformTenantSchema),
    nextCursor: z.uuid().nullable(),
});
export const platformTenantQuerySchema = z.object({
    q: z.string().trim().max(100).optional(),
    status: tenantStatus.optional(),
    plan: z.string().max(40).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const supportGrantSchema = z.object({
    id: z.uuid(),
    grantedBy: z.uuid(),
    note: z.string(),
    expiresAt: z.iso.datetime(),
    revokedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const platformTenantDetailSchema = platformTenantSchema.extend({
    account: accountStateSchema.nullable(),
    usage: z.array(z.object({ key: z.string(), limit: z.number().int().nullable(), usage: z.number().int() })),
    features: z.record(z.string(), z.boolean()),
    overrides: z.array(z.object({
        key: z.string(),
        value: z.union([z.number().int(), z.boolean()]).nullable(),
        reason: z.string(),
    })),
    flags: z.array(z.object({ key: z.string(), enabled: z.boolean(), tenantOverride: z.boolean() })),
    notes: z.array(z.object({ id: z.uuid(), body: z.string(), adminUserId: z.uuid(), createdAt: z.iso.datetime() })),
    supportGrants: z.array(supportGrantSchema),
    invoices: z.array(platformInvoiceSchema),
});
const tenantParams = idParamsSchema;
const tenantAction = (name, path, summary, body) => defineEndpoint({
    ...tag,
    name,
    method: "POST",
    path: `/v1/platform/tenants/{id}/${path}`,
    access: platform,
    summary,
    params: tenantParams,
    body,
    status: 200,
    response: platformTenantDetailSchema,
});
export const listPlatformTenants = defineEndpoint({
    ...tag,
    name: "listPlatformTenants",
    method: "GET",
    path: "/v1/platform/tenants",
    access: platform,
    summary: "Every business: status, plan, MRR, activity",
    query: platformTenantQuerySchema,
    status: 200,
    response: platformTenantListSchema,
});
export const getPlatformTenant = defineEndpoint({
    ...tag,
    name: "getPlatformTenant",
    method: "GET",
    path: "/v1/platform/tenants/{id}",
    access: platform,
    summary: "One business in detail (the read is audited)",
    params: tenantParams,
    status: 200,
    response: platformTenantDetailSchema,
});
export const changeTenantPlan = tenantAction("changeTenantPlan", "plan", "Move the business to another plan", z.object({ planKey: z.string().min(1).max(40), reason }));
export const extendTenantTrial = tenantAction("extendTenantTrial", "extend-trial", "Give the business more trial days", z.object({ days: z.number().int().min(1).max(90), reason }));
export const suspendTenant = tenantAction("suspendTenant", "suspend", "Suspend the business", z.object({ reason }));
export const reactivateTenant = tenantAction("reactivateTenant", "reactivate", "Lift a suspension or restriction", z.object({ reason }));
export const setTenantOverride = tenantAction("setTenantOverride", "overrides", "Set an entitlement override", z.object({
    key: z.string().min(1).max(60),
    value: z.union([z.number().int().min(0), z.boolean()]).nullable(),
    expiresAt: z.iso.datetime().nullable().default(null),
    reason,
}));
export const clearTenantOverride = tenantAction("clearTenantOverride", "overrides/clear", "Remove an entitlement override", z.object({ key: z.string().min(1).max(60), reason }));
export const setTenantFlag = tenantAction("setTenantFlag", "flags", "Turn a feature flag on or off for the business", z.object({ key: z.string().min(1).max(60), enabled: z.boolean(), reason }));
export const addTenantNote = tenantAction("addTenantNote", "notes", "Add an internal note", z.object({ body: z.string().trim().min(1).max(2000) }));
export const supportSessionSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: z.iso.datetime(),
    tenantId: z.uuid(),
});
export const openSupportSession = defineEndpoint({
    ...tag,
    name: "openSupportSession",
    method: "POST",
    path: "/v1/platform/tenants/{id}/support-session",
    access: platform,
    summary: "A support token for the business, while its owner's grant is valid",
    params: tenantParams,
    body: z.object({ reason }),
    status: 201,
    response: supportSessionSchema,
});
// Support grants (tenant side, SA-16) ---------------------------------------------------------------------------
export const supportGrantListSchema = z.object({ data: z.array(supportGrantSchema) });
export const listSupportGrants = defineEndpoint({
    tag: "support",
    name: "listSupportGrants",
    method: "GET",
    path: "/v1/support-grants",
    access: { permission: "support.grant" },
    summary: "Support access given to Sobthik",
    status: 200,
    response: supportGrantListSchema,
});
export const createSupportGrant = defineEndpoint({
    tag: "support",
    name: "createSupportGrant",
    method: "POST",
    path: "/v1/support-grants",
    access: { permission: "support.grant" },
    summary: "Let Sobthik support into this business for a while",
    body: z.object({ hours: z.number().int().min(1).max(72), note: z.string().trim().max(500).default("") }),
    status: 201,
    response: supportGrantSchema,
});
export const revokeSupportGrant = defineEndpoint({
    tag: "support",
    name: "revokeSupportGrant",
    method: "POST",
    path: "/v1/support-grants/{id}/revoke",
    access: { permission: "support.grant" },
    summary: "End support access now",
    params: idParamsSchema,
    status: 200,
    response: supportGrantSchema,
});
// Announcements (SA-19) -----------------------------------------------------------------------------------------
export const announcementSchema = z.object({
    id: z.uuid(),
    titleEn: z.string(),
    titleBn: z.string(),
    bodyEn: z.string(),
    bodyBn: z.string(),
    level: z.enum(["info", "warning"]),
    /** null = every business. */
    tenantIds: z.array(z.uuid()).nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
});
export const announcementListSchema = z.object({ data: z.array(announcementSchema) });
export const createAnnouncementSchema = z.object({
    titleEn: z.string().trim().min(1).max(120),
    titleBn: z.string().trim().min(1).max(120),
    bodyEn: z.string().trim().max(1000).default(""),
    bodyBn: z.string().trim().max(1000).default(""),
    level: z.enum(["info", "warning"]).default("info"),
    tenantIds: z.array(z.uuid()).min(1).nullable().default(null),
    startsAt: z.iso.datetime().optional(),
    endsAt: z.iso.datetime().nullable().default(null),
});
export const listCurrentAnnouncements = defineEndpoint({
    tag: "announcements",
    name: "listCurrentAnnouncements",
    method: "GET",
    path: "/v1/announcements",
    access: { signedIn: true },
    summary: "Announcements from Sobthik for this business, now",
    status: 200,
    response: announcementListSchema,
});
export const listPlatformAnnouncements = defineEndpoint({
    ...tag,
    name: "listPlatformAnnouncements",
    method: "GET",
    path: "/v1/platform/announcements",
    access: platform,
    summary: "Every announcement",
    status: 200,
    response: announcementListSchema,
});
export const createAnnouncement = defineEndpoint({
    ...tag,
    name: "createAnnouncement",
    method: "POST",
    path: "/v1/platform/announcements",
    access: platform,
    summary: "Announce to every business or to chosen ones",
    body: createAnnouncementSchema,
    status: 201,
    response: announcementSchema,
});
export const endAnnouncement = defineEndpoint({
    ...tag,
    name: "endAnnouncement",
    method: "POST",
    path: "/v1/platform/announcements/{id}/end",
    access: platform,
    summary: "Stop showing an announcement",
    params: idParamsSchema,
    status: 200,
    response: announcementSchema,
});
//# sourceMappingURL=platform-admin.js.map